import { Box, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertToLowerCase } from "../../utils/helperFns";
import useTextboxStyle from "./Textbox.styles";

interface TextBoxProps {
  name?: string;
  placeHolder?: string;
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  maxCharLength?: any;
  state?: string;
  handleOnBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  borderColor?: string;
  isEmailExist?: boolean;
  isDisabled?: boolean;
  backgroundColor?: string;
  // New prop for type
  type?: string;
  // Id of the element holding this field's visible label, for callers that render the
  // label outside the input (e.g. a separate grid cell).
  ariaLabelledBy?: string;
}

const TextBox = ({
  name = "",
  placeHolder = "",
  handleChange,
  maxCharLength,
  state = "",
  handleOnBlur,
  error = false,
  helperText = "",
  backgroundColor,
  isDisabled = false,
  // Default value for type is 'textbox'
  type = "textbox",
  ariaLabelledBy,
}: TextBoxProps) => {
  const { t } = useTranslation();
  const classes = useTextboxStyle();
  const [restOfChar, setRestOfChar] = useState({
    lengthOfState: 0,
    restOfLength: 0,
    reachLimit: false,
  });
  const { restOfLength, reachLimit } = restOfChar;

  // Function to handle the remaining character length calculation
  const handleLength = (valueData = "") => {
    if (maxCharLength) {
      const lengthOfChar = convertToLowerCase(valueData).length;
      const rest = valueData ? Math.max(maxCharLength - lengthOfChar, 0) : 0;

      setRestOfChar({
        restOfLength: rest,
        lengthOfState: lengthOfChar,
        reachLimit: Number(lengthOfChar) >= Number(maxCharLength),
      });
    }
  };

  // Function to handle the change in input value
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;

    if (type === "numberbox" && value) {
      // Only allow positive numbers and enforce max length
      value = value.replace(/[^0-9]/g, "");
      if (maxCharLength && value.length > maxCharLength) {
        value = value.slice(0, maxCharLength);
      }
    }

    if (handleChange) {
      event.target.value = value;
      handleChange(event);
    }
    handleLength(value);
  };

  // Effect to handle setting the initial state value
  useEffect(() => {
    if (!name || state === undefined || state === null) return;
    // getElementById matches the literal id — querySelector(`#${name}`) would
    // parse dotted DAM keys like "etg.course.name" as id+class selectors.
    const inputElement: any = document.getElementById(name);
    if (inputElement) {
      inputElement.value = state;
      handleLength(state);
    }
  }, [state, name]);

  // Function to handle keydown event
  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "F") {
      e.stopPropagation();
    }
  };

  return (
    <Box mr={2} sx={{ marginRight: 0 }}>
      <TextField
        variant='filled'
        size='small'
        name={name}
        placeholder={placeHolder}
        fullWidth
        id={name}
        error={error}
        helperText={helperText}
        disabled={isDisabled}
        type={type === "numberbox" ? "number" : "text"} // Set the type of the TextField
        sx={{
          input: { cursor: isDisabled ? "not-allowed" : undefined },
          backgroundColor: backgroundColor,
        }}
        inputProps={{
          maxLength: type === "textbox" ? maxCharLength : undefined, // Only apply maxLength for text input
          readOnly: false,
          "aria-labelledby": ariaLabelledBy,
        }}
        onChange={onChange}
        onBlur={handleOnBlur}
        onKeyDown={handleKeydown}
        // role=alert only while showing an error, so validation messages are announced
        // without turning the character-count hint into a live region.
        FormHelperTextProps={{
          className: classes.typotool,
          ...(error ? { role: "alert" } : {}),
        }}
      />

      {maxCharLength && (
        <Typography variant='p4regular' sx={{ marginTop: "10px" }}>
          {reachLimit ? (
            <>0 {`${t("characters")} ${t("left")}`}</>
          ) : (
            <>
              {restOfLength
                ? `${restOfLength} ${t("characters")} ${t("left")}`
                : `${maxCharLength} ${t("characters")} ${t("max")}`}
            </>
          )}
        </Typography>
      )}
    </Box>
  );
};

export default TextBox;
